import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { eur, dateTimeIt } from "@/lib/format";
import { PageHeader, EmptyState, Field } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Inbox, Search, Phone, Mail, MapPin, Home, UserPlus, Calculator } from "lucide-react";

export const LEAD_STATUSES = ["NUOVO", "In lavorazione", "Qualificato", "Non idoneo", "Convertito"];
const STATUS_CLS = {
  "NUOVO": "bg-primary/10 text-primary border-primary/20",
  "In lavorazione": "bg-amber-50 text-amber-700 border-amber-200",
  "Qualificato": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Non idoneo": "bg-slate-100 text-slate-500 border-slate-200",
  "Convertito": "bg-cyan-50 text-cyan-700 border-cyan-200",
};

function StatusBadge({ status }) {
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_CLS[status] || STATUS_CLS.NUOVO}`}>{status}</span>;
}

const photoUrl = (p) => (/^https?:\/\//.test(p) ? p : `https://www.investart.it/${String(p).replace(/^\//, "")}`);

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === "" || value === false) return null;
  return (
    <div className="flex justify-between gap-4 border-b border-border/60 py-1.5 text-sm">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-slate-800 text-right break-words">{value === true ? "Sì" : String(value)}</dd>
    </div>
  );
}

export default function Leads() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/leads").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => rows.filter((l) => {
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (search) {
      const hay = `${l.nome || ""} ${l.cognome || ""} ${l.email || ""} ${l.telefono || ""} ${l.comune || ""} ${l.provincia || ""} ${l.tipo_immobile || ""}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  }), [rows, search, statusFilter]);

  const changeStatus = async (id, status) => {
    try {
      const r = await api.put(`/leads/${id}/status`, { status });
      setSelected((s) => (s && s.id === id ? r.data : s));
      toast.success("Stato lead aggiornato");
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const convertLead = async (lead) => {
    try {
      const r = await api.post(`/leads/${lead.id}/convert`);
      toast.success(r.data.already ? "Contatto già collegato a questo lead" : "Contatto creato dal lead");
      setSelected((s) => (s && s.id === lead.id ? { ...s, contact_id: r.data.contact.id, status: "Convertito" } : s));
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="leads-page">
      <PageHeader title="Lead" subtitle={`${rows.length} lead ricevuti dal sito investart.it`} />

      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Cerca per nome, email, telefono, comune, tipologia..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="leads-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="sm:w-56" data-testid="leads-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-muted-foreground">Caricamento...</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Inbox} title="Nessun lead" description="I lead inviati dal sito investart.it compariranno qui automaticamente." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nominativo</TableHead>
                <TableHead>Immobile</TableHead>
                <TableHead>Zona</TableHead>
                <TableHead className="text-right">Prezzo richiesto</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Ricevuto</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((l) => (
                <TableRow key={l.id} className="cursor-pointer" onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                  <TableCell>
                    <div className="font-medium text-slate-800">{l.nome} {l.cognome}</div>
                    <div className="text-xs text-muted-foreground">{l.email}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>{l.tipo_immobile || "—"}</div>
                    <div className="text-xs text-muted-foreground">{l.mq ? `${l.mq} mq` : ""}{l.locali ? ` · ${l.locali} locali` : ""}</div>
                  </TableCell>
                  <TableCell className="text-sm">{l.comune}{l.provincia ? ` (${l.provincia})` : ""}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{l.prezzo_richiesto ? eur(l.prezzo_richiesto) : "—"}</TableCell>
                  <TableCell><StatusBadge status={l.status} /></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{dateTimeIt(l.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lead-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between gap-3 pr-6">
                  <span>{selected.nome} {selected.cognome}</span>
                  <StatusBadge status={selected.status} />
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-2">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-xs text-muted-foreground">Cambia stato:</span>
                  <Select value={selected.status} onValueChange={(v) => changeStatus(selected.id, v)}>
                    <SelectTrigger className="w-52" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => convertLead(selected)} disabled={!!selected.contact_id} data-testid="convert-lead-btn">
                    <UserPlus className="h-4 w-4 mr-2" />{selected.contact_id ? "Contatto creato" : "Converti in Contatto"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/valutazione?purchase=${selected.prezzo_richiesto || 0}`)} data-testid="lead-evaluate-btn">
                    <Calculator className="h-4 w-4 mr-2" />Avvia valutazione
                  </Button>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="rounded-lg border border-border p-4">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />Recapiti</h4>
                    <dl>
                      <DetailRow label="Telefono" value={selected.telefono} />
                      <DetailRow label="Email" value={selected.email} />
                      <DetailRow label="Indirizzo" value={selected.indirizzo} />
                      <DetailRow label="Comune" value={`${selected.comune || ""}${selected.provincia ? ` (${selected.provincia})` : ""}`} />
                      <DetailRow label="CAP" value={selected.cap} />
                      <DetailRow label="Fuori area" value={selected.fuori_area} />
                    </dl>
                  </div>
                  <div className="rounded-lg border border-border p-4">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Home className="h-3.5 w-3.5" />Immobile</h4>
                    <dl>
                      <DetailRow label="Tipologia" value={selected.tipo_immobile} />
                      <DetailRow label="Superficie" value={selected.mq ? `${selected.mq} mq` : ""} />
                      <DetailRow label="Locali" value={selected.locali} />
                      <DetailRow label="Bagni" value={selected.bagni} />
                      <DetailRow label="Piano" value={selected.piano} />
                      <DetailRow label="Ascensore" value={selected.ascensore} />
                      <DetailRow label="Balcone/Terrazzo" value={selected.balcone_terrazzo} />
                      <DetailRow label="Garage" value={selected.garage} />
                      <DetailRow label="Cantina" value={selected.cantina} />
                      <DetailRow label="Giardino" value={selected.giardino} />
                      <DetailRow label="Stato immobile" value={selected.stato_immobile} />
                      <DetailRow label="Situazione" value={selected.situazione} />
                      <DetailRow label="Prezzo richiesto" value={selected.prezzo_richiesto ? eur(selected.prezzo_richiesto) : ""} />
                    </dl>
                  </div>
                </div>

                {selected.note && (
                  <div className="rounded-lg border border-border p-4">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Note</h4>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{selected.note}</p>
                  </div>
                )}

                {Array.isArray(selected.foto) && selected.foto.length > 0 && (
                  <div className="rounded-lg border border-border p-4">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Foto ({selected.foto.length})</h4>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {selected.foto.map((p, i) => (
                        <a key={i} href={photoUrl(p)} target="_blank" rel="noreferrer" className="block aspect-square rounded-md overflow-hidden border border-border bg-muted" data-testid={`lead-photo-${i}`}>
                          <img src={photoUrl(p)} alt={`foto ${i + 1}`} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />Origine: {selected.source || "sito"}</span>
                  {selected.external_id && <span>ID sito: {selected.external_id}</span>}
                  {selected.site_created_at && <span>Creato sul sito: {selected.site_created_at}</span>}
                  <span>Ricevuto: {dateTimeIt(selected.created_at)}</span>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
